import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import * as api from 'api/api'
import { onUnauthorized, tokenStore } from 'api/client'
import { Me } from 'api/types'
import { disablePush } from 'lib/push'
import { SessionExpiredModal } from 'pages/system/SessionExpiredModal'

interface AuthState {
  user: Me | null
  loading: boolean
  signIn: (email: string, password: string, remember?: boolean) => Promise<void>
  setSession: (token: string, user: Me, remember?: boolean) => void
  signOut: () => void
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<Me | null>(null)
  const [loading, setLoading] = useState(Boolean(tokenStore.get()))
  const [sessionExpired, setSessionExpired] = useState(false)

  const signOut = useCallback(() => {
    disablePush()
    tokenStore.clear()
    setUser(null)
  }, [])

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) return
    try {
      setUser(await api.fetchMe())
    } catch {
      signOut()
    }
  }, [signOut])

  useEffect(() => {
    // Distinct from a deliberate sign-out: the session expired or was revoked server-side. Instead of
    // silently signing out, surface a modal over the current page — RequireAuth (App.tsx) only bounces
    // to /login once the user acknowledges and signOut() actually clears the session.
    onUnauthorized(() => setSessionExpired(true))
    refresh().finally(() => setLoading(false))
  }, [refresh])

  const setSession = useCallback((token: string, me: Me, remember = true) => {
    tokenStore.set(token, remember)
    setUser(me)
  }, [])

  const signIn = useCallback(
    async (email: string, password: string, remember = true) => {
      const { token, user: me } = await api.login(email, password)
      setSession(token, me, remember)
    },
    [setSession]
  )

  const value = useMemo(
    () => ({ loading, refresh, setSession, signIn, signOut, user }),
    [loading, refresh, setSession, signIn, signOut, user]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sessionExpired ? (
        <SessionExpiredModal
          onDismiss={() => {
            setSessionExpired(false)
            signOut()
          }}
        />
      ) : null}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
